import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

interface ContextTurn {
  user: string;
  assistant: string;
  resolvedQuestion?: string;
  matchedQuestion?: string;
  score?: number | null;
}

interface ContextPlan {
  mode: string;
  originalQuestion: string;
  resolvedQuestions: string[];
  reason: string;
}

interface ContextBackendResult {
  question: string;
  matchedQuestion?: string;
  matchedAnswer?: string;
  similarityScore?: number | null;
  liveHandoffSuggested?: boolean;
}

interface ContextBackendResponse {
  sessionId?: string;
  currentQuestion?: string;
  mode?: string;
  reason?: string;
  answerText?: string;
  matchedQuestion?: string;
  similarityScore?: number | null;
  resolvedQuestions?: string[];
  results?: ContextBackendResult[];
}

interface LabResult {
  question: string;
  answer: string;
  matchedQuestion?: string;
  score?: number | null;
  raw?: unknown;
}

@Component({
  selector: 'app-rnd-context-agent-lab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './rnd-context-agent-lab.component.html',
  styleUrls: ['./rnd-context-agent-lab.component.scss'],
})
export class RndContextAgentLabComponent {
  currentQuestion = 'یہ کیسے ملے گا؟';
  manualUser = 'گرین سرٹیفکیٹ کیا ہے؟';
  manualAssistant = 'گرین پراپرٹی سرٹیفکیٹ ایک سرکاری دستاویز ہے جو ملکیت اور ریکارڈ کی تصدیق کرتا ہے۔';
  sessionId = `rnd-context-${Date.now()}`;
  language: 'ur' | 'en' = 'ur';
  history: ContextTurn[] = [
    {
      user: 'گرین سرٹیفکیٹ کیا ہے؟',
      assistant: 'گرین پراپرٹی سرٹیفکیٹ ایک سرکاری دستاویز ہے جو ملکیت اور ریکارڈ کی تصدیق کرتا ہے۔',
      resolvedQuestion: 'گرین سرٹیفکیٹ کیا ہے؟',
    },
  ];
  plan: ContextPlan | null = null;
  results: LabResult[] = [];
  rawResponse = '';
  errorMessage = '';
  running = false;

  examples = [
    'یہ کیسے ملے گا؟',
    'اس کے لیے کون سی معلومات چاہیے؟',
    'واقعی؟',
    'کیا مطلب؟',
    'میں شکایت کیسے درج کروں اور گرین سرٹیفکیٹ کیا ہے',
    'اگر تم جواب نہ دے سکے تو کیا ہو گا',
  ];

  constructor(private readonly http: HttpClient) {}

  addHistoryTurn(): void {
    const user = this.manualUser.trim();
    const assistant = this.manualAssistant.trim();
    if (!user || !assistant) return;
    this.history = [...this.history, { user, assistant }].slice(-5);
    this.manualUser = '';
    this.manualAssistant = '';
  }

  removeTurn(index: number): void {
    this.history = this.history.filter((_, idx) => idx !== index);
  }

  clearHistory(): void {
    this.history = [];
    this.results = [];
    this.plan = null;
    this.rawResponse = '';
  }

  useExample(example: string): void {
    this.currentQuestion = example;
  }

  async run(): Promise<void> {
    const question = this.currentQuestion.trim();
    if (!question || this.running) return;

    this.running = true;
    this.errorMessage = '';
    this.results = [];
    this.rawResponse = '';

    try {
      const raw = await this.callContextAnswer(question);
      this.plan = {
        mode: raw?.mode || 'FAQ',
        originalQuestion: question,
        resolvedQuestions: raw?.resolvedQuestions?.length ? raw.resolvedQuestions : [question],
        reason: raw?.reason || 'Backend context endpoint returned no reason.',
      };

      const responses: LabResult[] = raw?.results?.length
        ? raw.results.map(item => ({
            question: item.question,
            answer: item.matchedAnswer || 'No answer returned.',
            matchedQuestion: item.matchedQuestion,
            score: typeof item.similarityScore === 'number' ? item.similarityScore : null,
            raw: item,
          }))
        : [{
            question,
            answer: raw?.answerText || 'No answer returned.',
            matchedQuestion: raw?.matchedQuestion,
            score: typeof raw?.similarityScore === 'number' ? raw.similarityScore : null,
            raw,
          }];

      this.results = responses;
      this.rawResponse = JSON.stringify(raw, null, 2);
      const combinedAnswer = responses.map((item, index) => responses.length > 1 ? `${index + 1}. ${item.answer}` : item.answer).join('\n\n');
      this.history = [...this.history, {
        user: question,
        assistant: combinedAnswer,
        resolvedQuestion: this.plan?.resolvedQuestions.join(' | '),
        matchedQuestion: responses.map(item => item.matchedQuestion).filter(Boolean).join(' | '),
        score: responses[0]?.score ?? null,
      }].slice(-5);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Context lab request failed.';
    } finally {
      this.running = false;
    }
  }

  private buildContextPlan(question: string): ContextPlan {
    const direct = this.isBotCapabilityQuestion(question);
    if (direct) {
      return {
        mode: 'DIRECT',
        originalQuestion: question,
        resolvedQuestions: [question],
        reason: 'The user is asking what happens when the bot cannot answer, so the agent returns a policy response without FAQ lookup.',
      };
    }

    const compound = this.expandKnownCompoundQuestions(question) || this.splitMultiQuestion(question);
    if (compound.length > 1) {
      return {
        mode: 'SPLIT',
        originalQuestion: question,
        resolvedQuestions: compound.map(part => this.resolveFollowUp(part)),
        reason: 'The question contains multiple intents, so each intent is sent separately to getAnswer.',
      };
    }

    const resolved = this.resolveFollowUp(question);
    const isFollowUp = resolved !== question;
    return {
      mode: isFollowUp ? 'FOLLOW_UP' : 'FAQ',
      originalQuestion: question,
      resolvedQuestions: [resolved],
      reason: isFollowUp
        ? 'The question uses a short reference such as یہ / اس / how / what, so it is expanded using the latest useful context.'
        : 'No context rewrite was needed; the question can go directly to getAnswer.',
    };
  }

  private resolveFollowUp(question: string): string {
    const cleanQuestion = question.trim();
    if (!this.isFollowUpQuestion(cleanQuestion)) return cleanQuestion;

    const previousTopic = this.pickPreviousTopic();
    if (!previousTopic) return cleanQuestion;

    if (/^(یہ|اس|اس کا|اس کی|اس کے|it|this|that)\b/i.test(cleanQuestion)) {
      return `${previousTopic} ${cleanQuestion}`.trim();
    }

    return `${previousTopic} ${cleanQuestion}`.trim();
  }

  private pickPreviousTopic(): string {
    const latest = [...this.history].reverse().find(turn => turn.matchedQuestion || turn.resolvedQuestion || turn.user);
    return (latest?.matchedQuestion || latest?.resolvedQuestion || latest?.user || '').trim();
  }

  private expandKnownCompoundQuestions(question: string): string[] | null {
    const normalized = question.replace(/\s+/g, ' ').trim().toLowerCase();
    const asksComplaintRegistration = /(شکایت|complaint).*(درج|رجسٹر|register|submit|file)/i.test(normalized)
      || /(درج|رجسٹر|register|submit|file).*(شکایت|complaint)/i.test(normalized);
    const asksGreenCertificate = /(گرین\s*سرٹیفکیٹ|green\s*(property\s*)?certificate)/i.test(normalized);

    if (asksComplaintRegistration && asksGreenCertificate) {
      return this.language === 'ur'
        ? ['شکایت کیسے درج کروں؟', 'گرین سرٹیفکیٹ کیا ہے؟']
        : ['How can I register a complaint?', 'What is a green certificate?'];
    }

    return null;
  }

  private splitMultiQuestion(question: string): string[] {
    const normalized = question.replace(/\s+/g, ' ').trim();
    const connectorPattern = /\s+(?:اور|and|also)\s+/i;
    if (!connectorPattern.test(normalized)) return [normalized];

    const parts = normalized.split(connectorPattern).map(part => part.trim()).filter(part => part.length >= 4);
    if (parts.length < 2) return [normalized];

    return parts.filter(part => this.looksLikeQuestion(part)).length >= 2 ? parts : [normalized];
  }

  private looksLikeQuestion(text: string): boolean {
    const normalized = text.trim().toLowerCase();
    return /[?؟]$/.test(normalized)
      || /(کیا|کیسے|کب|کہاں|کون|کیوں|کتنی|کتنا|how|what|when|where|who|why|can|do|does|is|are)\b/i.test(normalized);
  }

  private isFollowUpQuestion(question: string): boolean {
    const normalized = question.trim().toLowerCase();
    return /^(یہ|اس|اس کا|اس کی|اس کے|اس کو|اس میں|اس پر|وہ|اسی|کیسے|واقعی|کیا مطلب|مطلب|مزید|وضاحت|سمجھائیں|what|how|really|why|explain|tell me more|what about|how about|it|this|that)\b/i.test(normalized);
  }

  private isBotCapabilityQuestion(question: string): boolean {
    const normalized = question.trim().toLowerCase();
    return /(اگر|if).*(جواب|answer).*(نہ|not|نہیں|unable|can't|cannot|could not)/i.test(normalized)
      || /(تم|آپ|you).*(جواب نہ دے|جواب نہیں|نہیں سمجھ|not answer|cannot answer|can't answer|could not answer)/i.test(normalized);
  }

  private directCapabilityAnswer(): string {
    return this.language === 'ur'
      ? 'اگر مجھے پُراعتماد جواب نہ ملے تو میں لائیو ایجنٹ یا سپورٹ افسر سے رابطے کا اختیار دکھاؤں گا۔ آپ سوال کو دوبارہ واضح انداز میں بھی پوچھ سکتے ہیں۔'
      : 'If I cannot find a confident answer, I will offer live-agent or support options. You can also rephrase the question clearly.';
  }

  private async callGetAnswer(userQuestion: string): Promise<any> {
    const headers = new HttpHeaders({
      'X-Skip-Spinner': 'true',
      'X-Skip-Auth': 'true',
    });
    const url = `${environment.apiHost.replace(/\/$/, '')}/api/v1/chat/getAnswer`;
    return firstValueFrom(this.http.post<any>(url, {
      userQuestion,
      sessionId: this.sessionId,
      language: this.language,
      userId: null,
    }, { headers }));
  }

  private async callContextAnswer(currentQuestion: string): Promise<ContextBackendResponse> {
    const headers = new HttpHeaders({
      'X-Skip-Spinner': 'true',
      'X-Skip-Auth': 'true',
    });
    const url = `${environment.apiHost.replace(/\/$/, '')}/api/v1/chat/getAnswer/context`;
    return firstValueFrom(this.http.post<ContextBackendResponse>(url, {
      sessionId: this.sessionId,
      currentQuestion,
      language: this.language,
      userId: null,
      history: this.history.slice(-6).map(turn => ({
        user: turn.user,
        assistant: turn.assistant,
      })),
    }, { headers }));
  }

  private extractAnswer(raw: any): string {
    return raw?.matchedAnswer || raw?.answer || raw?.data?.matchedAnswer || raw?.data?.answer || 'No answer returned.';
  }
}
